<script>
  import { createEventDispatcher, onMount } from "svelte";
  import moment from "moment";
  import { CAMPAIGN_STATUS, computeCampaignStatus } from "../../../../utils/statusEngine.js";
  import { getStatusInfo } from "../../../../utils/freebetsCore.js";
  import { loadAnakatechClients } from "../anakatech/utils/anakatechCatalogs.js";

  moment.locale("es");

  export let api = {
    listCampaigns: async () => {
      throw new Error("api.listCampaigns no inyectado");
    },
  };
  export let provider = "ank";
  export let filters = {
    client: "",
    campaignName: "",
    type: "",
    statuses: [CAMPAIGN_STATUS.ACTIVE, CAMPAIGN_STATUS.SCHEDULED],
  };
  export let icons = {};
  export let timeFormat = "DD/MM, hh:mmA";

  const dispatch = createEventDispatcher();

  const TYPE_LABELS = {
    FREE_BET: "Free Bet",
    CASHBACK: "Cashback",
    RISK_FREE_BET: "Risk Free Bet",
  };

  let rows = [];
  let campaigns = [];
  let isLoading = false;
  let errorMessage = "";
  let page = 1;
  let pageSize = 10;
  let pageSizeOptions = [10, 50, 100, 200];
  let pagerInfo = { total: 0, totalPages: 1, pageStart: 0, pageEnd: 0 };
  let sortKey = "#";
  let sortAsc = false;
  let mounted = false;
  let loadSequence = 0;
  let lastFetchToken = "";

  const norm = (value) =>
    String(value ?? "")
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .toLowerCase()
      .trim();

  const typeLabel = (value) => TYPE_LABELS[value] || value || "—";

  function configurationState(doc) {
    const mode = String(doc?.assignationMode ?? "").trim().toUpperCase();
    const bonusType = String(doc?.providerCampaignType ?? doc?.bonusType ?? "").trim().toUpperCase();
    const requiresBonusAmount = ["FREE_BET", "RISK_FREE_BET"].includes(bonusType);
    const amountRaw = doc?.sportsConfig?.bonusAmount;
    const amount = Number(amountRaw);
    const missing = [];
    if (!["AUTO", "MANUAL"].includes(mode)) missing.push("método de asignación");
    if (mode === "AUTO" && requiresBonusAmount && (amountRaw === null || amountRaw === undefined || amountRaw === "" || !Number.isFinite(amount) || amount <= 0)) {
      missing.push("monto del bono");
    }
    return {
      complete: missing.length === 0,
      tooltip: missing.length ? `Falta configurar: ${missing.join(" y ")}` : "Configuración completa",
    };
  }

  function normalizeCampaign(doc) {
    const startMs = doc?.startDate ? new Date(doc.startDate).getTime() : 0;
    const endMs = doc?.endDate ? new Date(doc.endDate).getTime() : 0;
    const baseStatus = Number(
      doc?.campaignStatus ?? (doc?.isDisabled ? 0 : doc?.status ?? 1),
    );
    const statusCode = computeCampaignStatus(baseStatus, startMs, endMs);
    const statusInfo = getStatusInfo(statusCode);
    const configuration = configurationState(doc);

    return {
      ...doc,
      clientLabel:
        doc?.client?.clientName ||
        doc?.client?.name ||
        doc?.client?.clientCode ||
        doc?.client?.code ||
        doc?.brand ||
        "—",
      currencyLabel: doc?.currency || "—",
      bonusType: doc?.providerCampaignType || "",
      baseStatus,
      statusCode,
      statusInfoClass: statusInfo.class,
      configurationComplete: configuration.complete,
      configurationTooltip: configuration.tooltip,
      statusInfoLabel: statusInfo.label,
    };
  }

  async function clientParams() {
    const { allowedCodes } = await loadAnakatechClients();
    if (allowedCodes === null) return {};
    if (!allowedCodes.length) return { noAccess: true };
    return { clientCodes: allowedCodes.join(",") };
  }

  export async function refresh() {
    const sequence = ++loadSequence;
    isLoading = true;
    errorMessage = "";

    try {
      const access = await clientParams();
      if (access.noAccess) {
        rows = [];
        return;
      }

      const params = {};
      if (access.clientCodes) params.clientCodes = access.clientCodes;
      if (filters.client) params.clientCodes = filters.client;

      const response = await api.listCampaigns(provider, params);
      if (sequence !== loadSequence) return;

      const payload = response?.data ?? response;
      const raw = payload?.list ?? payload ?? [];
      rows = (Array.isArray(raw) ? raw : [])
        .filter(
          (campaign) =>
            String(campaign?.campaignType ?? "").toUpperCase() === "SPORTS",
        )
        .map(normalizeCampaign);
    } catch (error) {
      if (sequence !== loadSequence) return;
      console.error("Error cargando campañas Sport Bonus de Anakatech:", error);
      rows = [];
      errorMessage = "No se pudo cargar la lista de campañas Sport Bonus.";
    } finally {
      if (sequence === loadSequence) isLoading = false;
    }
  }

  function setSort(key, preferredAsc = null) {
    if (sortKey === key) {
      sortAsc = !sortAsc;
    } else {
      sortKey = key;
      sortAsc = preferredAsc ?? true;
    }
  }

  function sortValue(row, key) {
    switch (key) {
      case "client":
        return norm(row.clientLabel);
      case "campaign":
        return norm(row.campaignName);
      case "type":
        return norm(row.providerCampaignType);
      case "currency":
        return norm(row.currencyLabel);
      case "start":
        return row.startDate ? new Date(row.startDate).getTime() : 0;
      case "end":
        return row.endDate ? new Date(row.endDate).getTime() : 0;
      case "status":
        return Number(row.statusCode);
      default:
        return 0;
    }
  }

  function sortRows(source) {
    const sorted = [...source];
    if (sortKey === "#") {
      if (!sortAsc) sorted.reverse();
      return sorted;
    }
    sorted.sort((left, right) => {
      const a = sortValue(left, sortKey);
      const b = sortValue(right, sortKey);
      const result =
        typeof a === "string" ? a.localeCompare(b) : Number(a) - Number(b);
      return sortAsc ? result : -result;
    });
    return sorted;
  }

  $: fetchToken = JSON.stringify({
    provider,
    client: filters.client,
  });

  $: if (mounted && fetchToken !== lastFetchToken) {
    lastFetchToken = fetchToken;
    page = 1;
    refresh();
  }

  $: {
    const campaignNeedle = norm(filters.campaignName);
    const filtered = rows.filter((row) => {
      const campaignOk =
        !campaignNeedle || norm(row.campaignName).includes(campaignNeedle);
      const typeOk =
        !filters.type ||
        String(row.providerCampaignType).toUpperCase() ===
          String(filters.type).toUpperCase();
      const selectedStatuses = Array.isArray(filters.statuses)
        ? filters.statuses.map(Number).filter(Number.isFinite)
        : [];
      const statusOk =
        !selectedStatuses.length || selectedStatuses.includes(Number(row.statusCode));
      return campaignOk && typeOk && statusOk;
    });

    const sorted = sortRows(filtered);
    const total = sorted.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    if (page > totalPages) page = totalPages;
    const pageStart = (page - 1) * pageSize;
    const pageEnd = Math.min(pageStart + pageSize, total);

    campaigns = sorted.slice(pageStart, pageEnd);
    pagerInfo = { total, totalPages, pageStart, pageEnd };
  }

  onMount(() => {
    mounted = true;
  });
</script>

{#if isLoading}
  <p class="state-message">Cargando campañas Sport Bonus…</p>
{:else if errorMessage}
  <p class="state-message error">{errorMessage}</p>
{:else if campaigns.length === 0}
  <p class="state-message">No se encontraron campañas Sport Bonus.</p>
{:else}
  <div class="table-wrapper">
    <table class="table table-striped table-hover table-bordered align-middle">
      <thead>
        <tr>
          <th class="th-sort" on:click={() => setSort("#")}>
            N° <span class="hdr-ico">{@html icons.sort || ""}</span>
          </th>
          <th class="th-sort" on:click={() => setSort("client", true)}>
            Cliente <span class="hdr-ico">{@html icons.sort || ""}</span>
          </th>
          <th class="th-sort" on:click={() => setSort("campaign", true)}>
            Campaña <span class="hdr-ico">{@html icons.sort || ""}</span>
          </th>
          <th class="th-sort" on:click={() => setSort("type", true)}>
            Tipo <span class="hdr-ico">{@html icons.sort || ""}</span>
          </th>
          <th class="th-sort" on:click={() => setSort("currency", true)}>
            Moneda <span class="hdr-ico">{@html icons.sort || ""}</span>
          </th>
          <th class="th-sort" on:click={() => setSort("start", false)}>
            Inicio <span class="hdr-ico">{@html icons.sort || ""}</span>
          </th>
          <th class="th-sort" on:click={() => setSort("end", false)}>
            Fin <span class="hdr-ico">{@html icons.sort || ""}</span>
          </th>
          <th class="th-sort" on:click={() => setSort("status", false)}>
            Estado <span class="hdr-ico">{@html icons.sort || ""}</span>
          </th>
          <th>Sincronizado</th>
          <th>Opciones</th>
        </tr>
      </thead>
      <tbody>
        {#each campaigns as campaign, index (campaign.id || campaign.campaignId)}
          <tr class:configuration-pending={!campaign.configurationComplete}>
            <td>{pagerInfo.pageStart + index + 1}</td>
            <td>{campaign.clientLabel}</td>
            <td>
              <div>{campaign.campaignName || "—"}</div>
              {#if !campaign.configurationComplete}
                <span class="configuration-badge" title={campaign.configurationTooltip}>Configuración pendiente</span>
              {/if}
            </td>
            <td>{typeLabel(campaign.providerCampaignType)}</td>
            <td>{campaign.currencyLabel}</td>
            <td title={campaign.startDate ? moment(campaign.startDate).format("dddd D [de] MMMM YYYY, hh:mm A") : ""}>
              <span class="date-cell">{campaign.startDate ? moment(campaign.startDate).format(timeFormat) : "—"}</span>
            </td>
            <td title={campaign.endDate ? moment(campaign.endDate).format("dddd D [de] MMMM YYYY, hh:mm A") : ""}>
              <span class="date-cell">{campaign.endDate ? moment(campaign.endDate).format(timeFormat) : "—"}</span>
            </td>
            <td>
              <span class={`badge status-badge rounded-pill ${campaign.statusInfoClass}`}>
                <span class="status-dot"></span>
                {campaign.statusInfoLabel}
              </span>
            </td>
            <td>
              <span class="date-cell">{campaign.syncedAt ? moment(campaign.syncedAt).format(timeFormat) : "—"}</span>
            </td>
            <td class="actions-cell">
              <button
                type="button"
                class="btn-icon players-button"
                title={campaign.configurationComplete ? "Ver jugadores" : campaign.configurationTooltip}
                disabled={Number(campaign.statusCode) === 0 || !campaign.configurationComplete}
                on:click={() => dispatch("addplayers", campaign)}
              >
                {@html icons.persons || "+"}
              </button>
              <button
                type="button"
                class:pending-config={!campaign.configurationComplete}
                class="btn-icon edit-button"
                title={campaign.configurationComplete ? "Editar configuración" : campaign.configurationTooltip}
                on:click={() => dispatch("editconfig", campaign)}
              >
                {@html icons.edit || "✎"}
              </button>
              <button
                type="button"
                class="btn-icon delete-button"
                title="Eliminar campaña manualmente"
                aria-label="Eliminar campaña manualmente"
                disabled={Number(campaign.statusCode) === 0}
                on:click={() => dispatch("delete", campaign)}
              >
                {@html icons.delete || "×"}
              </button>
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
{/if}

<div class="pagination-bar">
  <span class="total-badge">Total: <strong>{pagerInfo.total}</strong></span>
  <div class="page-controls">
    <button on:click={() => (page = 1)} disabled={page === 1} aria-label="Primera página">«</button>
    <button on:click={() => (page = Math.max(1, page - 1))} disabled={page === 1} aria-label="Página anterior">‹</button>
    <span>Página <strong>{page}</strong> de <strong>{pagerInfo.totalPages}</strong></span>
    <button on:click={() => (page = Math.min(pagerInfo.totalPages, page + 1))} disabled={page >= pagerInfo.totalPages} aria-label="Página siguiente">›</button>
    <button on:click={() => (page = pagerInfo.totalPages)} disabled={page >= pagerInfo.totalPages} aria-label="Última página">»</button>
  </div>
  <select bind:value={pageSize} on:change={() => { pageSize = Number(pageSize); page = 1; }}>
    {#each pageSizeOptions as option}
      <option value={option}>{option} / pág.</option>
    {/each}
  </select>
</div>

<style>
  .state-message {
    margin: 1rem;
    color: #6c757d;
    text-align: center;
  }
  .state-message.error {
    color: #dc3545;
  }
  .table-wrapper {
    max-height: 70vh;
    margin: 0 10px;
    overflow: auto;
    border: 1px solid var(--bs-border-color, #e5e7eb);
    border-radius: 0.5rem;
  }
  .table-wrapper table {
    margin-bottom: 0;
  }
  .th-sort {
    cursor: pointer;
    user-select: none;
    white-space: nowrap;
  }
  .hdr-ico {
    display: inline-flex;
    margin-left: 0.35rem;
    opacity: 0.65;
  }
  thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    padding: 0.6rem 0.5rem;
    border-bottom: 0;
    background: #fff;
    box-shadow: 0 1px 0 #dee2e6;
    color: #555;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }
  tbody td {
    padding: 0.35rem 0.5rem;
    vertical-align: middle;
    font-size: 0.85rem;
  }
  tr.configuration-pending > td {
    background-color: #fffdf5;
  }
  .configuration-badge {
    display: inline-flex;
    margin-top: 0.2rem;
    padding: 0.15rem 0.45rem;
    border: 1px solid #ffda6a;
    border-radius: 999px;
    background: #fff3cd;
    color: #664d03;
    font-size: 0.68rem;
    font-weight: 600;
    white-space: nowrap;
  }
  .date-cell {
    white-space: nowrap;
  }
  .status-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.35em 0.75em;
    font-size: 0.75rem;
    font-weight: 600;
  }
  .status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    opacity: 0.85;
  }
  .actions-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
  }
  .btn-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: 1px solid transparent;
    border-radius: 8px;
    cursor: pointer;
  }
  .players-button {
    border-color: #cfe2ff;
    background: #f0f7ff;
    color: #0d6efd;
  }
  .players-button:hover:not(:disabled) {
    border-color: #0d6efd;
    background: #0d6efd;
    color: #fff;
  }
  .edit-button {
    border-color: #ffe69c;
    background: #fff9e6;
    color: #997404;
  }
  .edit-button:hover:not(:disabled),
  .edit-button.pending-config {
    border-color: #ffc107;
    background: #ffc107;
    color: #332701;
  }
  .delete-button {
    border-color: #f1aeb5;
    background: #fff5f5;
    color: #dc3545;
  }
  .delete-button:hover:not(:disabled) {
    border-color: #dc3545;
    background: #dc3545;
    color: #fff;
  }
  .btn-icon:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
  .pagination-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin: 0.5rem 10px 0;
    padding: 0.65rem 0.75rem;
    border: 1px solid var(--bs-border-color, #e5e7eb);
    border-radius: 0.5rem;
    background: #fff;
  }
  .total-badge,
  .page-controls,
  .pagination-bar select {
    color: #555;
    font-size: 0.8rem;
  }
  .page-controls {
    display: flex;
    align-items: center;
    gap: 0.35rem;
  }
  .page-controls button {
    width: 34px;
    height: 34px;
    padding: 0;
    border: 1px solid var(--bs-border-color, #e5e7eb);
    border-radius: 0.4rem;
    background: #fff;
  }
  .page-controls button:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
  .pagination-bar select {
    padding: 0.3rem 0.55rem;
    border: 1px solid var(--bs-border-color, #e5e7eb);
    border-radius: 0.4rem;
    background: #fff;
  }
  @media (max-width: 720px) {
    .pagination-bar {
      flex-direction: column;
    }
  }
</style>
